import React, { useState } from "react";
import { Formik, Field, Form } from "formik";
import { ToastContainer, toast } from "react-toastify";
import * as Yup from "yup";
import Button from "../../components/Button/Button";
import "react-toastify/dist/ReactToastify.css";
import {
  ContainerWrapper,
  HeaderContainer,
  ContactLayout,
  ContactInfoSection,
  ContactInfoCard,
  FormContainer,
  FormHeader,
  InputContainer,
  ButtonWrapper,
  RequiredNote,
  SuccessMessage,
} from "./Contact.style.js";
import InputField from "../../components/InputField/InputField";
import TextArea from "../../components/TextArea/TextArea";
import Header from "../../components/Header/Header";
import baseUrl from "../../api/config";

const CustomInputField = ({ field, form, ...props }) => {
  const fieldName = field.name;
  const errorMessage = form.errors[fieldName];
  const isTouched = form.touched[fieldName];
  return (
    <InputContainer>
      <InputField
        {...field}
        {...props}
        isError={!!isTouched && !!errorMessage}
        errorMessage={isTouched && errorMessage}
      />
    </InputContainer>
  );
};

const CustomTextArea = ({ field, form, ...props }) => {
  const fieldName = field.name;
  const errorMessage = form.errors[fieldName];
  const isTouched = form.touched[fieldName];
  return (
    <InputContainer>
      <TextArea
        {...field}
        {...props}
        isError={!!isTouched && !!errorMessage}
        errorMessage={isTouched && errorMessage}
      />
    </InputContainer>
  );
};

export default function Contact() {
  const [isSubmitted, setSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  React.useEffect(() => {
    document.title = "Contact Us — TrixieGroup & Association";
  }, []);

  const formUrl = `${baseUrl.API_URL}/forms/form_02`;

  const submitForm = (values, { resetForm }) => {
    setSubmitting(true);
    const bodyData = {
      msgToRecipient: {
        subject: "Message From Trixie Group",
        message:
          "Your message has been sent successfully, we will get back to you shortly",
        companyName: "Trixie Group",
      },
      companyName: "Trixie Group",
      adminEmailAddress: "info@trixiegroup.com",
      data: {
        name: values.name,
        emailAddress: values.email,
        message: values.message,
      },
    };

    fetch(formUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(bodyData),
    })
      .then((response) => response.json())
      .then((data) => {
        resetForm();
        setIsSuccess(true);
        toast.success(data?.message || "Message sent successfully!");
        setSubmitting(false);

        // Reset success state after 5 seconds
        setTimeout(() => setIsSuccess(false), 5000);
      })
      .catch((error) => {
        toast.error(error?.message || "Something went wrong");
        setSubmitting(false);
      });
  };

  const contactInfo = [
    {
      icon: "📍",
      label: "Head Office",
      value: (
        <a
          href="https://www.google.com/maps/search/?api=1&query=Ise+Eledumare+Shopping+Complex,+Okebola+off+Iyana+Ogunpa+Road,+Ibadan,+Nigeria"
          target="_blank"
          rel="noopener noreferrer"
        >
          Ise Eledumare Shopping Complex, Okebola off Iyana Ogunpa Road, Ibadan, Nigeria
        </a>
      ),
    },
    {
      icon: "🏢",
      label: "Branch Office",
      value: (
        <a
          href="https://www.google.com/maps/search/?api=1&query=Road+2,+Ire+Akari+Estate,+off+Akala+Express+Road,+Oluyole,+Ibadan,+Nigeria"
          target="_blank"
          rel="noopener noreferrer"
        >
          Road 2, Ire Akari Estate, off Akala Express Road, Oluyole, Ibadan, Nigeria
        </a>
      ),
    },
    {
      icon: "📞",
      label: "Phone",
      value: (
        <>
          <a href="tel:+2348034231676">+234 803 423 1676</a>
          <br />
          <a href="tel:+2348053056078">+234 805 305 6078</a>
        </>
      ),
    },
    {
      icon: "✉️",
      label: "Email",
      value: <a href="mailto:info@trixiegroup.com">info@trixiegroup.com</a>,
    },
    {
      icon: "📱",
      label: "Follow Us",
      value: (
        <>
          <a
            href="https://www.facebook.com/profile.php?id=100086018196742"
            target="_blank"
            rel="noopener noreferrer"
          >
            Facebook
          </a>
          {" | "}
          <a
            href="https://www.instagram.com/trixiegroup/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Instagram
          </a>
          {" | "}
          <a
            href="https://www.tiktok.com/@trixiesolar3"
            target="_blank"
            rel="noopener noreferrer"
          >
            TikTok
          </a>
        </>
      ),
    },
  ];

  return (
    <>
      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      <ContainerWrapper>
        <HeaderContainer>
          <Header
            title="Get In Touch"
            subtitle="Have questions about our solar or security solutions? We'd love to hear from you. Send us a message and we'll respond as soon as possible."
            showAccent={true}
          />
        </HeaderContainer>

        <ContactLayout>
          <ContactInfoSection>
            {contactInfo.map((info, index) => (
              <ContactInfoCard key={index} data-aos="fade-up" data-aos-delay={index * 100}>
                <div className="icon-wrapper">
                  <span className="icon">{info.icon}</span>
                </div>
                <div className="content">
                  <div className="label">{info.label}</div>
                  <div className="value">{info.value}</div>
                </div>
              </ContactInfoCard>
            ))}
          </ContactInfoSection>

          <FormContainer data-aos="fade-up" data-aos-delay="200">
            {isSuccess ? (
              <SuccessMessage>
                <div className="success-icon">✓</div>
                <h3>Message Sent!</h3>
                <p>Thank you for reaching out. We'll get back to you shortly.</p>
              </SuccessMessage>
            ) : (
              <>
                <FormHeader>
                  <h3>Send us a Message</h3>
                  <p>Fill out the form below and we'll respond within 24 hours.</p>
                </FormHeader>

                <RequiredNote>
                  Fields marked with <span className="asterisk">*</span> are required
                </RequiredNote>

                <Formik
                  initialValues={{
                    email: "",
                    name: "",
                    message: "",
                  }}
                  validationSchema={Yup.object({
                    email: Yup.string()
                      .email("Please enter a valid email address")
                      .required("Email is required"),
                    name: Yup.string()
                      .min(2, "Name must be at least 2 characters")
                      .required("Name is required"),
                    message: Yup.string()
                      .min(10, "Message must be at least 10 characters")
                      .max(500, "Message must be 500 characters or less")
                      .required("Message is required"),
                  })}
                  onSubmit={submitForm}
                >
                  <Form>
                    <Field
                      name="name"
                      type="text"
                      label="Your Name"
                      placeholder="Enter your full name"
                      component={CustomInputField}
                      required
                    />
                    <Field
                      name="email"
                      type="email"
                      label="Email Address"
                      placeholder="Enter your email address"
                      component={CustomInputField}
                      required
                    />
                    <Field
                      name="message"
                      label="Your Message"
                      placeholder="Tell us about your project or inquiry..."
                      component={CustomTextArea}
                      required
                    />
                    <ButtonWrapper>
                      <Button
                        disabled={isSubmitted}
                        label={isSubmitted ? "Sending..." : "Send Message"}
                        type="submit"
                      />
                    </ButtonWrapper>
                  </Form>
                </Formik>
              </>
            )}
          </FormContainer>
        </ContactLayout>
      </ContainerWrapper>
    </>
  );
}
